import { useState } from 'react'
import { toast } from 'sonner'
import { Dialog } from 'radix-ui'
import { useConfirm } from '@/components/common/ConfirmDialog'
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import { BoolIcon } from '@/components/common/BoolIcon'

const CODE_REGEX = /^[A-Za-z][A-Za-z0-9_]{0,63}$/

export type ColumnConfig<T> = {
  key: keyof T | string
  header: string
  render?: (item: T) => React.ReactNode
  className?: string
}

export type FormFieldConfig = {
  key: string
  label: string
  type: 'text' | 'code' | 'number' | 'boolean'
  required?: boolean
  validate?: (value: string) => string | null
}

export type DictionaryPanelConfig<T extends { code: string; isActive: boolean }> = {
  title: string
  description?: string
  addLabel?: string
  useList: () => UseQueryResult<T[]>
  useCreate: () => UseMutationResult<T, Error, Record<string, unknown>>
  useUpdate: () => UseMutationResult<T, Error, { code: string; patch: Record<string, unknown> }>
  useDeactivate: () => UseMutationResult<unknown, Error, string>
  columns: ColumnConfig<T>[]
  formFields: FormFieldConfig[]
  /**
   * Delete semantics - controls action button label + confirm dialog wording.
   *  - 'hard' (default): backend usuwa rekord. Label "Usuń", dialog "Usunąć X?".
   *  - 'soft': backend ustawia is_active=false, rekord zachowany. Label "Dezaktywuj",
   *    dialog "Dezaktywować X?". Per architecture decision per BC (np. action types
   *    + document types są soft - referencje w istniejących taskach/plikach).
   */
  deleteSemantics?: 'hard' | 'soft'
}

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Blad zapisu'
}

// ---------------------------------------------------------------------------
// Row form dialog - generic over field config
// ---------------------------------------------------------------------------

type RowDialogProps<T extends { code: string; isActive: boolean }> = {
  editing: T | null
  onClose: () => void
  config: DictionaryPanelConfig<T>
}

function RowDialog<T extends { code: string; isActive: boolean }>({ editing, onClose, config }: RowDialogProps<T>) {
  const create = config.useCreate()
  const update = config.useUpdate()

  const initValues = (): Record<string, string | boolean> => {
    const init: Record<string, string | boolean> = {}
    for (const f of config.formFields) {
      if (f.key === 'code') {
        init[f.key] = editing ? String((editing as Record<string, unknown>)[f.key] ?? '') : ''
      } else if (f.type === 'boolean') {
        init[f.key] = editing ? Boolean((editing as Record<string, unknown>)[f.key]) : true
      } else {
        init[f.key] = editing ? String((editing as Record<string, unknown>)[f.key] ?? '') : ''
      }
    }
    return init
  }

  const [values, setValues] = useState<Record<string, string | boolean>>(initValues)
  const [error, setError] = useState<string | null>(null)

  const setValue = (key: string, val: string | boolean) =>
    setValues(prev => ({ ...prev, [key]: val }))

  const submit = () => {
    setError(null)

    // Validate all fields
    for (const f of config.formFields) {
      if (f.type === 'code' && !editing) {
        const v = String(values[f.key] ?? '')
        if (!CODE_REGEX.test(v)) {
          setError(`${f.label}: litery, cyfry, podkreślenia. Musi zaczynać się od litery.`)
          return
        }
      }
      if (f.required && f.type !== 'boolean') {
        const v = String(values[f.key] ?? '').trim()
        if (!v) { setError(`${f.label} nie może być puste.`); return }
      }
      if (f.validate) {
        const msg = f.validate(String(values[f.key] ?? ''))
        if (msg) { setError(msg); return }
      }
    }

    if (editing) {
      // Build patch - exclude 'code' field (not patchable)
      const patch: Record<string, unknown> = {}
      for (const f of config.formFields) {
        if (f.key === 'code') continue
        patch[f.key] = f.type === 'boolean' ? Boolean(values[f.key]) : values[f.key]
      }
      update.mutate(
        { code: editing.code, patch },
        { onSuccess: onClose, onError: (e) => setError(formatError(e)) },
      )
    } else {
      const payload: Record<string, unknown> = {}
      for (const f of config.formFields) {
        payload[f.key] = f.type === 'boolean' ? Boolean(values[f.key]) : values[f.key]
      }
      create.mutate(
        payload as Record<string, unknown>,
        { onSuccess: onClose, onError: (e) => setError(formatError(e)) },
      )
    }
  }

  const isSubmitting = create.isPending || update.isPending
  const title = editing
    ? `Edycja: ${editing.code}`
    : `Nowy wpis - ${config.title}`

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[420px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">{title}</Dialog.Title>
          <div className="mt-4 flex flex-col gap-3 text-sm">
            {config.formFields.map(f => {
              if (f.type === 'boolean') {
                return (
                  <label key={f.key} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={Boolean(values[f.key])}
                      onChange={e => setValue(f.key, e.target.checked)}
                      disabled={isSubmitting}
                    />
                    <span>{f.label}</span>
                  </label>
                )
              }
              const isCodeField = f.type === 'code'
              return (
                <label key={f.key} className="grid gap-1">
                  <span className="text-xs uppercase text-slate-500">{f.label}</span>
                  <input
                    value={String(values[f.key] ?? '')}
                    onChange={e => setValue(f.key, e.target.value)}
                    disabled={(isCodeField && !!editing) || isSubmitting}
                    className={`border rounded px-2 py-1 ${isCodeField ? 'font-mono' : ''}`}
                    type={f.type === 'number' ? 'number' : 'text'}
                  />
                </label>
              )
            })}
            {error && <p className="text-red-600 text-xs">{error}</p>}
            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded" disabled={isSubmitting}>
                Anuluj
              </button>
              <button
                type="button"
                onClick={submit}
                className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ---------------------------------------------------------------------------
// DictionaryPanel<T> - main generic component
// ---------------------------------------------------------------------------

export function DictionaryPanel<T extends { code: string; isActive: boolean }>(
  config: DictionaryPanelConfig<T>,
) {
  const confirm = useConfirm()
  const q = config.useList()
  const deactivate = config.useDeactivate()
  const [editing, setEditing] = useState<T | null>(null)
  const [creating, setCreating] = useState(false)

  const items = q.data ?? []

  return (
    <section className="border rounded">
      <header className="flex items-center justify-between px-3 py-2 border-b bg-slate-50">
        <div>
          <h2 className="font-medium text-sm">{config.title}</h2>
          {config.description && (
            <p className="text-xs text-slate-500 mt-0.5">{config.description}</p>
          )}
        </div>
        <button
          onClick={() => setCreating(true)}
          className="text-xs bg-sky-600 text-white rounded px-2 py-1 shrink-0"
        >
          + {config.addLabel ?? 'Dodaj'}
        </button>
      </header>

      {q.isLoading && (
        <p className="text-center text-slate-400 py-6 text-sm">Ładowanie…</p>
      )}
      {q.isError && (
        <p className="text-center text-red-500 py-6 text-sm">Błąd ładowania danych.</p>
      )}

      {!q.isLoading && !q.isError && (
        <div className="overflow-x-auto">
          {items.length === 0 ? (
            <p className="text-center text-slate-500 py-8 text-sm">Brak wpisów - dodaj pierwszy.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-slate-50/50">
                  {config.columns.map(col => (
                    <th
                      key={String(col.key)}
                      className={`text-left px-3 py-1.5 font-medium text-slate-600 ${col.className ?? ''}`}
                    >
                      {col.header}
                    </th>
                  ))}
                  <th className="px-3 py-1.5 w-28 text-right font-medium text-slate-600">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.code} className="border-b last:border-0 hover:bg-slate-50/60">
                    {config.columns.map(col => (
                      <td key={String(col.key)} className={`px-3 py-2 ${col.className ?? ''}`}>
                        {col.render
                          ? col.render(item)
                          : col.key === 'isActive'
                            ? <BoolIcon value={item.isActive} />
                            : String((item as Record<string, unknown>)[String(col.key)] ?? '')}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => setEditing(item)}
                        className="text-slate-600 px-1 hover:text-sky-700"
                      >
                        Edytuj
                      </button>
                      <button
                        onClick={async () => {
                          const isSoft = config.deleteSemantics === 'soft'
                          const ok = await confirm({
                            title: isSoft
                              ? `Dezaktywować ${item.code}?`
                              : `Usunąć ${item.code}?`,
                            description: isSoft
                              ? 'Pozycja zostanie ukryta z list wyboru. Istniejące rekordy zachowają snapshot etykiety.'
                              : 'Operacja jest nieodwracalna. Jeśli wartość jest używana w innych rekordach, usunięcie nie powiedzie się.',
                            confirmLabel: isSoft ? 'Dezaktywuj' : 'Usuń',
                            variant: 'destructive',
                          })
                          if (ok) deactivate.mutate(item.code, { onError: e => toast.error(formatError(e)) })
                        }}
                        className="text-red-600 px-1 ml-1"
                      >
                        {config.deleteSemantics === 'soft' ? 'Dezaktywuj' : 'Usuń'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {(creating || editing) && (
        <RowDialog
          editing={editing}
          onClose={() => { setCreating(false); setEditing(null) }}
          config={config}
        />
      )}
    </section>
  )
}
